import { useMutation } from '@apollo/client';
import { useState } from 'react';

import type { ConfirmEngagementHoursInput } from '@/__generated__/graphql';
import Button from '@/componentsv2/Button';
import { firstErrorCode } from '@/core/api';
import { getDefaultErrorMessage } from '@/core/apiErrorMessages';

import DisputeExpander from '../../components/EngagementHub/DisputeExpander';
import { CONFIRM_ENGAGEMENT_HOURS } from '../operations';
import { PartialConfirmAction } from './partialConfirm';

interface ConfirmActionsProps {
  submissionId: string;
  /** Names the expert in the dispute copy; neutral when unresolvable. */
  expertName: string;
  hoursSubmitted: number;
  /** Largest partial that can be confirmed: min(submitted, NTE remaining). */
  maxConfirmableHours: number;
}

/**
 * The work approver's actions on a `submitted` row: confirm in full, or
 * dispute.
 *
 * `DisputeExpander` is imported from the prototype rather than re-implemented —
 * it is scene-free pure props and its copy is locked, so a live twin would be a
 * copy of locked copy waiting to drift.
 *
 * Partial confirmation comes in from `partialConfirm.tsx` — everything about it
 * lives there so the pending ruling on the partial-hours fork stays a deletion.
 */
export default function ConfirmActions({
  submissionId,
  expertName,
  hoursSubmitted,
  maxConfirmableHours,
}: ConfirmActionsProps): JSX.Element {
  const [disputing, setDisputing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmHours, { loading }] = useMutation(CONFIRM_ENGAGEMENT_HOURS, {
    refetchQueries: ['engagementHub'],
  });

  async function run(input: ConfirmEngagementHoursInput, fallback: string) {
    setError(null);
    try {
      await confirmHours({ variables: { input } });
      setDisputing(false);
    } catch (err) {
      setError(getDefaultErrorMessage(firstErrorCode(err) ?? '') ?? fallback);
    }
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-wrap justify-end gap-8">
        <Button
          size="small"
          disabled={loading}
          onClick={() =>
            void run({ submissionId, mode: 'full' }, 'These hours could not be confirmed.')
          }
        >
          Confirm
        </Button>
        <Button
          variant="secondary"
          size="small"
          disabled={loading}
          onClick={() => {
            setError(null);
            setDisputing((open) => !open);
          }}
        >
          Dispute
        </Button>
        <PartialConfirmAction
          hoursSubmitted={hoursSubmitted}
          maxConfirmableHours={maxConfirmableHours}
          busy={loading}
          onConfirm={(hoursConfirmed, note) =>
            void run(
              { submissionId, mode: 'partial', hoursConfirmed, note },
              'These hours could not be confirmed.'
            )
          }
        />
      </div>

      {disputing ? (
        <DisputeExpander
          expertLabel={expertName}
          onSubmit={(note) =>
            void run(
              { submissionId, mode: 'dispute', note },
              'This submission could not be disputed.'
            )
          }
          onCancel={() => setDisputing(false)}
        />
      ) : null}

      {error ? <p className="text-red-600 body-2">{error}</p> : null}
    </div>
  );
}
